import React from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  List,
  ListItem,
  ListItemText,
  Typography
} from '@mui/material';

export interface ConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  title: string;
  message: string;
  details?: string[];
  confirmText?: string;
  cancelText?: string;
  confirmColor?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  confirmVariant?: 'text' | 'outlined' | 'contained';
  cancelColor?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  cancelVariant?: 'text' | 'outlined' | 'contained';
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  showCancel?: boolean;
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  details,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmColor = 'primary',
  confirmVariant = 'contained',
  cancelColor = 'secondary',
  cancelVariant = 'outlined',
  maxWidth = 'sm',
  showCancel = true
}) => {
  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    } else {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth>
      <DialogTitle
        sx={{
          fontSize: '1.5rem',
          fontWeight: 'bold'
        }}>
        {title}
      </DialogTitle>
      <DialogContent>
        <Typography
          variant="body1"
          sx={{ mb: details && details.length > 0 ? 2 : 0 }}>
          {message}
        </Typography>
        {details && details.length > 0 && (
          <List dense>
            {details.map((detail, index) => (
              <ListItem
                key={index}
                disablePadding>
                <ListItemText
                  primary={detail}
                  primaryTypographyProps={{
                    variant: 'body2',
                    color: 'text.secondary'
                  }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        {showCancel && (
          <Button
            onClick={onClose}
            variant={cancelVariant}
            color={cancelColor}>
            {cancelText}
          </Button>
        )}
        <Button
          onClick={handleConfirm}
          variant={confirmVariant}
          color={confirmColor}>
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
